import { html, nothing } from "lit";
import type { AgentFieldPatch } from "../controllers/agents-config.ts";
import type { AgentsListResult } from "../types.ts";
import { AGENT_HINTS } from "../control-hints.ts";

type AgentConfigEntry = {
  id?: string;
  name?: string;
  workspace?: string;
  agentDir?: string;
  identity?: { name?: string; emoji?: string; theme?: string };
};

export function renderAgentOverviewEditor(params: {
  agent: AgentsListResult["agents"][number];
  configEntry: AgentConfigEntry | undefined;
  hasConfigListEntry: boolean;
  workspacePreview: string;
  identityName: string;
  identityStatus: string;
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  onRegisterAgent: (agentId: string) => void;
  onAgentFieldChange: (agentId: string, field: AgentFieldPatch) => void;
}) {
  const {
    agent,
    configEntry,
    hasConfigListEntry,
    workspacePreview,
    identityName,
    identityStatus,
    configForm,
    configLoading,
    configSaving,
    onRegisterAgent,
    onAgentFieldChange,
  } = params;

  if (!hasConfigListEntry) {
    return html`
      <div class="callout warn" style="margin-top: 12px;">
        <div style="font-weight: 600;">Not in agents.list</div>
        <p class="muted" style="margin: 8px 0 0; font-size: 13px;">
          Register this agent to edit workspace, identity, and model defaults here.
        </p>
        <button
          type="button"
          class="btn btn--sm primary"
          style="margin-top: 10px;"
          ?disabled=${!configForm || configLoading || configSaving}
          title=${AGENT_HINTS.registerInList}
          aria-label=${AGENT_HINTS.registerInList}
          @click=${() => onRegisterAgent(agent.id)}
        >
          Add to agents.list
        </button>
      </div>
      <div class="agents-overview-grid" style="margin-top: 16px;">
        <div class="agent-kv">
          <div class="label">Workspace</div>
          <div class="mono">${workspacePreview}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Identity</div>
          <div>${identityName}</div>
          ${identityStatus ? html`<div class="agent-kv-sub muted">${identityStatus}</div>` : nothing}
        </div>
      </div>
    `;
  }

  const entryName = configEntry?.name ?? "";
  const entryWorkspace = configEntry?.workspace ?? "";
  const entryAgentDir = configEntry?.agentDir ?? "";
  const entryIdentityName = configEntry?.identity?.name ?? "";
  const entryIdentityEmoji = configEntry?.identity?.emoji ?? "";
  const entryIdentityTheme = configEntry?.identity?.theme ?? "";

  return html`
    <div class="agents-editor-fields" style="margin-top: 16px;">
      <label class="field">
        <span>Display name</span>
        <input
          .value=${entryName}
          ?disabled=${!configForm || configLoading || configSaving}
          @change=${(e: Event) =>
            onAgentFieldChange(agent.id, { name: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="field">
        <span>Workspace</span>
        <input
          class="mono"
          .value=${entryWorkspace}
          placeholder=${workspacePreview}
          ?disabled=${!configForm || configLoading || configSaving}
          @change=${(e: Event) =>
            onAgentFieldChange(agent.id, { workspace: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="field">
        <span>Agent directory</span>
        <input
          class="mono"
          .value=${entryAgentDir}
          ?disabled=${!configForm || configLoading || configSaving}
          @change=${(e: Event) =>
            onAgentFieldChange(agent.id, { agentDir: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="field">
        <span>Identity name</span>
        <input
          .value=${entryIdentityName}
          ?disabled=${!configForm || configLoading || configSaving}
          @change=${(e: Event) =>
            onAgentFieldChange(agent.id, { identityName: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="field">
        <span>Identity emoji</span>
        <input
          .value=${entryIdentityEmoji}
          ?disabled=${!configForm || configLoading || configSaving}
          @change=${(e: Event) =>
            onAgentFieldChange(agent.id, { identityEmoji: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label class="field">
        <span>Identity theme</span>
        <input
          .value=${entryIdentityTheme}
          ?disabled=${!configForm || configLoading || configSaving}
          @change=${(e: Event) =>
            onAgentFieldChange(agent.id, { identityTheme: (e.target as HTMLInputElement).value })}
        />
      </label>
    </div>
  `;
}
